import React, { Component } from "react";
import axios from "axios";
import { Table, Button, Modal, Form, Row, Col, Alert } from "react-bootstrap";
import CartContext from "./CartContext";

// 🔹 Rating mappings
const ratingMap = {
  1: "NeedImprovements",
  2: "Good",
  3: "Excellent",
  4: "OutStanding",
};

const reverseRatingMap = {
  NeedImprovements: 1,
  Good: 2,
  Excellent: 3,
  OutStanding: 4,
};

export default class EmployeePerformance extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      goals: [],
      employees: [],
      showModal: false,
      form: {
        id: null,
        employeeId: "",
        year: new Date().getFullYear(),
        goals: "",
        feedback: "",
        ratings: "",
      },
      filters: {
        employeeId: "",
        year: new Date().getFullYear(),
      },
      error: "",
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchEmployees();
    this.fetchGoals();
  }

  // 🔹 Fetch employees
  fetchEmployees = () => {
    axios
      .get(
        process.env.REACT_APP_API +
          "Employee/GetEmployees/" +
          this.context.storeId
      )
      .then((res) => this.setState({ employees: res.data }))
      .catch(() => this.setState({ error: "Failed to load employees." }));
  };

  // 🔹 Fetch goals with year + employee filter
  fetchGoals = () => {
    const { employeeId, year } = this.state.filters;
    axios
      .get(
        process.env.REACT_APP_API +
          `EmployeeGoalAndFeedback/${this.context.storeId}/${year}`
      )
      .then((res) => {
        let data = res.data.map((g) => ({
          ...g,
          ratings: ratingMap[g.ratings] || "",
        }));

        if (employeeId) {
          data = data.filter((g) => g.employeeId === employeeId);
        }

        this.setState({ goals: data });
      })
      .catch(() => this.setState({ error: "Failed to load goals." }));
  };

  // 🔹 Save or update goal
  handleSave = async () => {
    const { form } = this.state;

    const payload = {
      employeeId: form.employeeId || null,
      year: form.year ? Number(form.year) : null,
      goals: form.goals || "",
      feedback: form.feedback || "",
      ratings: reverseRatingMap[form.ratings] || null,
      storeId: this.context.storeId || null,
    };

    try {
      if (form.id) {
        await axios.put(
          `${process.env.REACT_APP_API}EmployeeGoalAndFeedback/${form.id}`,
          payload
        );
      } else {
        await axios.post(
          process.env.REACT_APP_API + "EmployeeGoalAndFeedback",
          payload
        );
      }
      this.fetchGoals();
      this.handleClose();
    } catch (err) {
      console.error("Save failed:", err.response?.data || err.message);
      this.setState({ error: "Save failed." });
    }
  };

  // 🔹 Delete goal
  handleDelete = (id) => {
    if (!window.confirm("Delete this record?")) return;
    axios
      .delete(process.env.REACT_APP_API + "EmployeeGoalAndFeedback/" + id)
      .then(() => this.fetchGoals())
      .catch(() => this.setState({ error: "Delete failed." }));
  };

  // 🔹 Form change
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      form: { ...prev.form, [name]: value },
    }));
  };

  // 🔹 Filter change
  handleFilterChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prev) => ({
        filters: { ...prev.filters, [name]: value },
      }),
      this.fetchGoals
    );
  };

  // 🔹 Modal open
  handleShow = (goal = null) => {
    if (goal) {
      this.setState({
        form: {
          id: goal.id,
          employeeId: goal.employeeId || "",
          year: goal.year,
          goals: goal.goals || "",
          feedback: goal.feedback || "",
          ratings: goal.ratings || "",
        },
        showModal: true,
      });
    } else {
      this.setState({
        form: {
          id: null,
          employeeId: "",
          year: new Date().getFullYear(),
          goals: "",
          feedback: "",
          ratings: "",
        },
        showModal: true,
      });
    }
  };

  // 🔹 Modal close
  handleClose = () => this.setState({ showModal: false });

  render() {
    const { goals, employees, form, filters, showModal, error } = this.state;

    return (
      <div className="container mt-1">
        <h3>
          <b>Employee Performance Management (Goals & Feedback)</b>
        </h3>
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Filters */}
        <Row className="mb-3 mt-4">
          <Col md={4}>
            <Form.Select
              name="employeeId"
              value={filters.employeeId}
              onChange={this.handleFilterChange}
            >
              <option value="">All Employees</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Select
              name="year"
              value={filters.year}
              onChange={this.handleFilterChange}
            >
              {Array.from({ length: 101 }, (_, i) => 2000 + i).map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4} className="text-end">
            <Button onClick={() => this.handleShow()}>+ Add Goal</Button>
          </Col>
        </Row>

        {/* Goals Table */}
        <Table bordered hover>
          <thead className="table-light">
            <tr>
              <th width="10%">Employee</th>
              <th width="10%">Year</th>
              <th width="30%">Goals</th>
              <th width="30%">Feedback</th>
              <th width="10%">Ratings</th>
              <th width="10%">Actions</th>
            </tr>
          </thead>
          <tbody>
            {goals.map((g) => {
              const employee = employees.find((e) => e.id === g.employeeId);
              return (
                <tr key={g.id}>
                  <td>
                    {employee
                      ? `${employee.firstName} ${employee.lastName}`
                      : "N/A"}
                  </td>
                  <td>{g.year}</td>
                  <td>{g.goals}</td>
                  <td>{g.feedback}</td>
                  <td>{g.ratings}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="warning"
                      onClick={() => this.handleShow(g)}
                    >
                      Edit
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => this.handleDelete(g.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>
              {form.id ? "Edit Goal & Feedback" : "Add Goal & Feedback"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Employee</Form.Label>
                <Form.Select
                  name="employeeId"
                  value={form.employeeId}
                  onChange={this.handleChange}
                >
                  <option value="">Select Employee</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.firstName} {e.lastName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Year</Form.Label>
                <Form.Select
                  name="year"
                  value={form.year}
                  onChange={this.handleChange}
                >
                  {Array.from({ length: 101 }, (_, i) => 2000 + i).map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Goals</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  name="goals"
                  value={form.goals}
                  onChange={this.handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Feedback</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="feedback"
                  value={form.feedback}
                  onChange={this.handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Ratings</Form.Label>
                <Form.Select
                  name="ratings"
                  value={form.ratings}
                  onChange={this.handleChange}
                >
                  <option value="">Select Rating</option>
                  <option value="NeedImprovements">Need Improvements</option>
                  <option value="Good">Good</option>
                  <option value="Excellent">Excellent</option>
                  <option value="OutStanding">OutStanding</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
